import { createAction, createSelector, Selector } from '@ngrx/store';
import { getActionName } from '@app/core/state/state-helpers';
import { PagedApiData } from '@app/core/models/PagedApiData';
import {
  ComplexSearchRequest,
  SearchRequest,
} from '@app/shared/search/interfaces/search-request';
import { SortPropDir } from '@swimlane/ngx-datatable';
import { TableDataState } from '@app/shared/table/helpers/generic-table-state';
import { createTableStateSelector } from '@app/shared/table/helpers/generic-table-selectors';

const loadTableData =
  <T>(actionPrefix: string) =>
  (index: string) =>
    createAction(
      getActionName(`Load ${index} table data`, actionPrefix),
      (
        limit: number,
        offset: number,
        complexSearchData: ComplexSearchRequest,
        searchData: SearchRequest,
        sort: SortPropDir,
        props: T = null,
      ) => ({
        index,
        limit,
        offset,
        searchData,
        complexSearchData,
        sort,
        props,
      }),
    );

const loadTableDataSuccess =
  <T>(actionPrefix: string) =>
  (index: string) =>
    createAction(
      getActionName(`Load ${index} table data success`, actionPrefix),
      (response: PagedApiData<T>) => ({ index, response }),
    );

const loadTableDataFailure = (actionPrefix: string) => (index: string) =>
  createAction(
    getActionName(`Load ${index} table data failure`, actionPrefix),
    (error: string) => ({ index, error }),
  );

const clearTableData = (actionPrefix: string) => (index: string) =>
  createAction(
    getActionName(`Clear ${index} table data`, actionPrefix),
    (maintainFilters: boolean = false) => ({ index, maintainFilters }),
  );
const updateRowData = (actionPrefix: string) => (index: string) =>
  createAction(
    getActionName(`Update ${index} row data`, actionPrefix),
    (
      rowIdColumnName: string,
      rowIdValue: any,
      updates: Record<string, any>[],
    ) => ({
      index,
      rowIdColumnName,
      rowIdValue,
      updates,
    }),
  );

export const genericTableDataActions = <ResponseType, PropType>(
  actionPrefix: string,
) => ({
  loadTableData: loadTableData<PropType>(actionPrefix),
  loadTableDataSuccess: loadTableDataSuccess<ResponseType>(actionPrefix),

  loadTableDataFailure: loadTableDataFailure(actionPrefix),
  clearTableData: clearTableData(actionPrefix),
  updateRowData: updateRowData(actionPrefix),
});

export class GenericTableActionsClass<
  ResponseType,
  PropType,
  StateType extends TableDataState,
> {
  constructor(
    private readonly prefix: string,
    private readonly tableIndex: string,
    private readonly selector: Selector<any, StateType>,
  ) {}

  private readonly actions = genericTableDataActions<ResponseType, PropType>(
    this.prefix,
  );
  public readonly load = this.actions.loadTableData(this.tableIndex);
  public readonly loadSuccess = this.actions.loadTableDataSuccess(
    this.tableIndex,
  );
  public readonly loadFailure = this.actions.loadTableDataFailure(
    this.tableIndex,
  );
  public readonly clear = this.actions.clearTableData(this.tableIndex);

  public readonly updateRowData = this.actions.updateRowData(this.tableIndex);

  public readonly selectTableState = createTableStateSelector(
    this.selector,
    this.tableIndex,
  );

  public readonly selectTableIsLoading = createSelector(
    this.selectTableState,
    (state) => state.isLoading,
  );
}

export class GenericTableActionsWithoutPropClass<
  ResponseType,
  StateType extends TableDataState,
> extends GenericTableActionsClass<ResponseType, never, StateType> {
  constructor(
    prefix: string,
    tableIndex: string,
    selector: Selector<any, StateType>,
  ) {
    super(prefix, tableIndex, selector);
  }
}
